import TrackElement from '@capsule/track-elements'
import FocusTrap from 'shared/assets/scripts/focusTrap'
import validateTarget from 'shared/assets/scripts/validate-target'

export default class NavigationPanel {
	private navigationPanel: HTMLElement | null
	private panelTriggers: NodeListOf<HTMLButtonElement>
	private triggerToFocusAfterClosing: HTMLButtonElement | null
	private id: string
	private hash: string

	constructor() {
		this.navigationPanel = document.querySelector('.navigationPanel')
		this.id = this.navigationPanel?.getAttribute('id') ?? 'menu'
		this.hash = `#${this.id}`
		this.panelTriggers = document.querySelectorAll(`[aria-labelledby="${this.id}"]`)
		this.triggerToFocusAfterClosing = null
		this.togglePanel = this.togglePanel.bind(this)
		this.onEscapePress = this.onEscapePress.bind(this)
		this.onPanelClick = this.onPanelClick.bind(this)
		this.onTriggerClick = this.onTriggerClick.bind(this)
		this.toggleDropdown = this.toggleDropdown.bind(this)
	}

	init() {
		if (!this.navigationPanel) return

		if (window.location.hash === this.hash) {
			this.openPanel()
		}

		this.addEvent()
		this.initFocusTrap()
	}

	addEvent() {
		for (const button of this.panelTriggers) {
			button.addEventListener('click', this.onTriggerClick)
		}

		this.navigationPanel?.addEventListener('click', this.onPanelClick)

		window.addEventListener('keydown', this.onEscapePress)
	}

	/**
	 * Close navigation panel when user press "escape"
	 * @param {KeyboardEvent} event
	 */
	onEscapePress(event: KeyboardEvent) {
		if (this.isOpen && event.key === 'Escape') {
			this.closePanel()
		}
	}

	/**
	 * Close panel when user click on panel backdrop
	 * @param {MouseEvent} event
	 */
	onPanelClick(event: MouseEvent) {
		if (event.target instanceof HTMLButtonElement) {
			const isDropdownButton = validateTarget({
				target: event.target,
				selectorString: '.navigationPanel-navigationItemDropdownButton',
				nodeName: ['button']
			})

			if (isDropdownButton) {
				this.toggleDropdown(event.target)
			}
		}

		if (event.target === this.navigationPanel) {
			this.closePanel()
		}
	}

	/**
	 * Toggle dropdown
	 * @param {HTMLButtonElement} button
	 */
	toggleDropdown(button: HTMLButtonElement) {
		const dropdownParent = button.closest('.navigationPanel-navigationItem')
		dropdownParent?.classList.toggle('isOpen')

		const dropdownIsOpen = dropdownParent?.classList.contains('isOpen')

		button.setAttribute(
			'aria-label',
			dropdownIsOpen ? 'Fermer le sous-menu' : 'Ouvrir le sous-menu'
		)
	}

	/**
	 * Toggle panel when user click on burger menu button
	 * Send tracking when user open the panel
	 * @param {MouseEvent} event
	 */
	onTriggerClick(event: MouseEvent) {
		if (!this.isOpen) {
			TrackElement.sendEvent(this.TrackingMenuBurgerOpen)
		}

		if (event.currentTarget instanceof HTMLButtonElement) {
			this.triggerToFocusAfterClosing = event.currentTarget
		}

		this.togglePanel()
	}

	togglePanel() {
		this.isOpen ? this.closePanel() : this.openPanel()
	}

	/**
	 * Close panel, replace history and give the focus to the last clicked trigger button
	 */
	closePanel() {
		if (!this.navigationPanel) return
		history.replaceState(null, '', window.location.pathname)
		this.updateUi()

		if (this.triggerToFocusAfterClosing) {
			this.triggerToFocusAfterClosing.focus()
			this.triggerToFocusAfterClosing = null
		}
	}

	/**
	 * Set hash and Update the UI
	 */
	openPanel() {
		if (!this.navigationPanel) return
		window.location.hash = this.hash
		this.updateUi()
	}

	/**
	 * Update the state UI of the panel and buttons
	 */
	updateUi() {
		if (!this.navigationPanel) return

		for (const button of this.panelTriggers) {
			button.classList.toggle('isActive', this.isOpen)
			button.setAttribute(
				'aria-label',
				this.isOpen ? 'Fermer le menu de navigation' : 'Ouvrir le menu de navigation'
			)
		}

		this.navigationPanel.classList.toggle('isOpen', this.isOpen)
		document.body.classList.toggle('isScrollLocked', this.isOpen)
	}

	/**
	 * Prevent focus to go outside the panel when it is open
	 */
	initFocusTrap() {
		if (!this.navigationPanel) return

		const panelFocusableElements: HTMLElement[] = Array.from(
			this.navigationPanel.querySelectorAll('a, button')
		)

		const firstFocusableElement = panelFocusableElements.at(0)
		const lastFocusableElement = panelFocusableElements.at(-1)

		if (firstFocusableElement && lastFocusableElement) {
			const focusTrap = new FocusTrap({
				parentContainer: this.navigationPanel,
				firstFocusableElement,
				lastFocusableElement
			})

			focusTrap.init()
		}
	}

	/**
	 * Tracking Event to send when user opens the panel
	 * @readonly
	 * @type {{ event: string; navigation_type: string; navigation_name: string; }}
	 */
	get TrackingMenuBurgerOpen() {
		return {
			event: 'navigation_click',
			navigation_type: 'menu-burger',
			navigation_name: 'menu-burger-open'
		}
	}

	/**
	 * Get the state of panel based on the presence of the hash
	 * @readonly
	 * @type {boolean}
	 */
	get isOpen() {
		return window.location.hash === this.hash
	}
}
